import React, { useEffect, useRef } from "react";

// Lightweight atmospheric particle field (Canvas 2D) that reacts to the mouse.
// Drifting amber / cream "dust" that gathers a parallax pull toward the cursor.
const COLORS = ["#d97925", "#b02418", "#f5f2eb", "#a3a09a"];

const AtmosphereParticles = ({ className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let raf;

    const mouse = { x: -9999, y: -9999, active: false };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(120, Math.floor((width * height) / 13000));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 4 + 2.5,
        wind: Math.random() * 0.5 + 0.35,
        fall: Math.random() * 0.25 + 0.1,
        swayAmp: Math.random() * 0.5 + 0.2,
        swaySpeed: Math.random() * 1.2 + 0.6,
        swayPhase: Math.random() * Math.PI * 2,
        baseAlpha: Math.random() * 0.18 + 0.06,
        phase: Math.random() * Math.PI * 2,
        pulse: Math.random() * 0.6 + 0.4,
        color: COLORS[(Math.random() * COLORS.length) | 0],
      }));
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    };

    let t = 0;
    const tick = () => {
      t += 0.016;
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = "lighter"; // additive glow
      for (const p of particles) {
        // snow-storm drift: prevailing wind + gentle downward fall + sway
        p.x += p.wind + Math.sin(t * p.swaySpeed + p.swayPhase) * p.swayAmp;
        p.y += p.fall + Math.cos(t * p.swaySpeed * 0.7 + p.swayPhase) * p.swayAmp * 0.4;

        // mouse interaction: subtle pull toward cursor + swirl
        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < 26000) {
            const dist = Math.sqrt(dist2) || 1;
            const force = (1 - dist / 161) * 0.6;
            p.x += (dx / dist) * force;
            p.y += (dy / dist) * force;
            p.x += (-dy / dist) * force * 0.4; // swirl
            p.y += (dx / dist) * force * 0.4;
          }
        }

        // wrap around edges
        if (p.x < -20) p.x = width + 20;
        else if (p.x > width + 20) p.x = -20;
        if (p.y < -20) p.y = height + 20;
        else if (p.y > height + 20) p.y = -20;

        // beacon pulse (like an aviation warning light) + proximity boost
        const beacon = 0.55 + 0.45 * Math.sin(t * p.pulse + p.phase);
        let alpha = p.baseAlpha * beacon;
        if (mouse.active) {
          const d2 = (mouse.x - p.x) ** 2 + (mouse.y - p.y) ** 2;
          if (d2 < 26000) alpha += (1 - d2 / 26000) * 0.25;
        }
        alpha = Math.min(alpha, 0.6);

        // soft glowing halo
        const glowR = p.r * 3;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowR);
        g.addColorStop(0, p.color);
        g.addColorStop(0.25, p.color);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowR, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();

        // bright core
        ctx.globalAlpha = Math.min(alpha * 2.2, 0.85);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 0.7, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(tick);
    };

    resize();
    if (!reduceMotion) raf = requestAnimationFrame(tick);
    else {
      // static draw once
      tick();
      cancelAnimationFrame(raf);
    }

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="atmosphere-particles"
      aria-hidden="true"
      className={`pointer-events-none ${className}`}
    />
  );
};

export default AtmosphereParticles;
